import { useState, type JSX } from "react";
import {
  type ICollaborator,
  type IColleague,
  useAssessmentStore,
  useContinueAssessment,
  useCreateAssessment,
} from "@/service/assessment";
import { BackButton, Button } from "@/shared/ui";
import { type ISelectOption, Select } from "../select";
import { StepCard } from "../step-card";
import { RadioGroup } from "../radio-group";

const isCollaborator = (item: ICollaborator | IColleague): item is ICollaborator => {
  return "projectDetails" in item;
};

const formatListToOptions = (data: (IColleague | ICollaborator)[] = []): ISelectOption[] => {
  return data.map((item) => {
    const totalHours = isCollaborator(item)
      ? item.projectDetails?.reduce((acc, item) => acc + item.overlap_hours, 0) || 0
      : null;

    return {
      value: item.fmno,
      label: `${item.firstName} ${item.lastName}, ${item.position} ${totalHours ? `(${totalHours} h)` : ""}`,
      status: isCollaborator(item) ? item.assessmentCompletionStatus : undefined,
    };
  });
};

interface InitialStepProps {
  colleagueList: IColleague[];
  collaboratorList: ICollaborator[];
  isLoadingNextStep: boolean;
}

export const InitialStep = (props: InitialStepProps): JSX.Element => {
  const { colleagueList, collaboratorList, isLoadingNextStep } = props;
  const { setAssessmentId } = useAssessmentStore();

  const [selectedColleague, setSelectedColleague] = useState<string | null>(null);

  const { mutate: createAssessment, isPending: isPendingCreate, error: createAssessmentError } = useCreateAssessment();
  const {
    mutate: continueAssessment,
    isPending: isPendingContinue,
    error: continueAssessmentError,
  } = useContinueAssessment();

  const isPendingData = isPendingCreate || isPendingContinue || isLoadingNextStep;

  const getAssessedPerson = (): ICollaborator | IColleague | null => {
    if (selectedColleague) {
      const found = colleagueList?.find((c: IColleague) => c.fmno === selectedColleague);

      if (found) return found;
    }

    return null;
  };

  const onSubmit = () => {
    const assessedPerson = getAssessedPerson();

    if (!assessedPerson) return;

    if (assessedPerson.draftAssessmentId) {
      const body = {
        assessedFmno: assessedPerson.fmno,
        assessmentId: assessedPerson.draftAssessmentId,
      };

      continueAssessment(body, {
        onSuccess: () => {
          setAssessmentId(assessedPerson.draftAssessmentId);
        },
        onError: (err) => {
          console.log("err", err.data?.detail);
        },
      });
    } else {
      const body = { assessedFmno: assessedPerson.fmno };

      createAssessment(body, {
        onSuccess: ({ assessmentId }) => {
          setAssessmentId(assessmentId);
        },
      });
    }
  };

  const onSelectCollaborator = (value: string) => {
    const collaborator = collaboratorList?.find((c: ICollaborator) => c.fmno === value);

    if (!collaborator) return;

    if (collaborator.draftAssessmentId) {
      const body = {
        assessedFmno: collaborator.fmno,
        assessmentId: collaborator.draftAssessmentId,
      };

      continueAssessment(body, {
        onSuccess: () => {
          setAssessmentId(collaborator.draftAssessmentId);
        },
      });
    } else {
      const body = { assessedFmno: collaborator.fmno };

      createAssessment(body, {
        onSuccess: ({ assessmentId }) => {
          setAssessmentId(assessmentId);
        },
      });
    }
  };

  const completedReviews = collaboratorList?.filter(
    (collaborator) => collaborator.assessmentCompletionStatus === "completed",
  );

  const activeCollaborators = collaboratorList?.filter(
    (collaborator) => collaborator.assessmentCompletionStatus !== "completed",
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <BackButton />
      <StepCard title="Choose a colleague" hideFooter>
        <div className="flex w-full flex-col">
          <div className="flex w-full grow items-start gap-8 px-10 pb-5">
            <div className="flex w-1/2 flex-col gap-3">
              <Select
                disabled={isPendingData}
                value={selectedColleague}
                onChange={setSelectedColleague}
                placeholder="Colleague"
                options={formatListToOptions(colleagueList)}
              />
              <Button
                disabled={!selectedColleague || isPendingData}
                onClick={onSubmit}
                size="sm"
                appearance="primary"
                className="!h-full w-[130px] !border-none !px-0 !py-5 text-center"
                isLoading={isPendingData}
              >
                Continue
              </Button>
              {(createAssessmentError?.data?.detail || continueAssessmentError?.data?.detail) && (
                <p className="font-family-third text-base font-medium text-[#EC221F]">
                  {createAssessmentError?.data?.detail || continueAssessmentError?.data?.detail}
                </p>
              )}
            </div>
            <div className="w-1/2 space-y-4">
              <h2 className="font-family-second text-2xl/[100%] font-bold">Your recent collaborators:</h2>
              <RadioGroup
                disabled={isPendingData}
                value={null}
                onChange={onSelectCollaborator}
                options={formatListToOptions(activeCollaborators)}
              />
            </div>
          </div>
          {completedReviews?.length > 0 && (
            <div className="border-background-grey border-t p-10">
              <p className="text-eerie-black text-sm">
                <span className="font-bold">Completed Reviews: </span>
                {completedReviews.map((reviewer) => `${reviewer.firstName} ${reviewer.lastName}`).join(", ")}
              </p>
            </div>
          )}
        </div>
      </StepCard>
    </div>
  );
};
